import type { FC, ImgHTMLAttributes } from "react";
import StoryblokImage from "./components/StoryblokImage";
import { getDimensionFromUrl } from "./image.utils";

type ImageProps = {
	/**
	 * An expected Storyblok url
	 */
	src: string;
	/**
	 * Classname to be appended on <img />
	 */
	className?: string;
	/**
	 * Fill out parent's dimensions - and default to object-fit:'cover'
	 */
	fill?: boolean;
	/**
	 * Set width for element. This will cause an "max-width" on the image.
	 */
	width?: number;
	/**
	 * Whether or not the image should be prioritized. This will set loading to 'eager'.
	 */
	priority?: boolean;
	/**
	 * How many iterations of srcSet's
	 */
	tolerance?: number;
	/**
	 * Should the placeholder div be hidden
	 */
	hidePlaceholder?: boolean;
	/**
	 * The aspect ratio of the image. Example: for 5:7 aspect ratio, pass aspectRatio={7/5}
	 */
	aspectRatio?: number;
} & ImgHTMLAttributes<HTMLImageElement>;

const Image: FC<ImageProps> = ({
	className,
	src,
	alt,
	width,
	height,
	fill = false,
	priority = false,
	tolerance = 1.5,
	loading = "lazy",
	sizes,
	hidePlaceholder = false,
	aspectRatio,
	...rest
}) => {
	const isStoryblokImage =
		src.startsWith("https://a.storyblok.com") &&
		Boolean(getDimensionFromUrl(src)[0]) &&
		Boolean(getDimensionFromUrl(src)[1]);

	return isStoryblokImage ? (
		<StoryblokImage
			{...{
				className,
				src,
				alt,
				width,
				height: height ? Number(height) : undefined,
				fill,
				priority,
				tolerance,
				loading,
				sizes,
				hidePlaceholder,
				aspectRatio,
				...rest,
			}}
		/>
	) : (
		<img
			loading={loading}
			alt={alt}
			height={height}
			width={width}
			src={src}
			{...rest}
		/>
	);
};

export default Image;
